import { Box, Divider } from '@mui/material';
import { useEffect } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import useAvailableDoses from '~/hooks/dose/useAvailableDoses';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { currentDoseState } from '~/state/atoms/dose/doseAtoms';
import { isJobRunning, jobQueueState } from '~/state/atoms/jobAtoms';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { WorkflowTypes, workflowModalState } from '~/state/atoms/viewerAtoms';
import AiAssistantButton from '../../AiAssistantButton';
import ImageHeader from '../ContouringStep/StructureSet/ImageHeader/ImageHeader';
import StructureSet from '../ContouringStep/StructureSet/StructureSet';
import StepHeader from '../StepHeader';
import DoseHeader from './DoseHeader/DoseHeader';
import DoseSelection from './EmptyDose/DoseSelection';

export default function DoseContent() {
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const currentDose = useRecoilValue(currentDoseState);
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const { setEnvironmentDose } = useViewportEnvironment();
    const availableDoses = useAvailableDoses(true);
    const setWorkflowModal = useSetRecoilState(workflowModalState);
    const jobQueue = useRecoilValue(jobQueueState);
    const hasActiveTasks = jobQueue.jobs.some((job) =>
        isJobRunning(job.status)
    );

    useEffect(() => {
        if (selectedPatientId && !currentDose && !hasActiveTasks) {
            setWorkflowModal((state) => ({
                ...state,
                open: true,
                page: WorkflowTypes.DOSE,
            }));
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDoseSelect = (selectedDose: DoseMatrix) => {
        setEnvironmentDose([selectedDose], selectedPatientId ?? '');
    };

    return (
        <Box
            height="100%"
            position="relative"
            display="grid"
            gridTemplateRows="auto 1fr"
        >
            <Box>
                <StepHeader titleId="workflow.dose" />
                <Box px={1.5} mt={1} mb={1.5}>
                    <AiAssistantButton page={WorkflowTypes.DOSE} />
                </Box>
                {!currentDose && availableDoses.length > 0 && (
                    <>
                        <Divider
                            sx={{
                                mx: 1.5,
                                my: 1,
                                fontSize: 11,
                                color: 'text.secondary',
                            }}
                        >
                            OR
                        </Divider>
                        <DoseSelection onSelect={handleDoseSelect} />
                    </>
                )}
                {currentDose && (
                    <>
                        <Divider sx={{ my: 1, mt: 2 }} />
                        <DoseHeader onDoseSelect={handleDoseSelect} />
                    </>
                )}
                <Divider sx={{ my: 1 }} />
                <ImageHeader />
            </Box>
            {selectedPatientId && currentStructureSet && (
                <Box height="100%" display="grid" gridTemplateRows="auto 1fr">
                    <Divider sx={{ mb: 1, mt: 1.5 }} />
                    <StructureSet structureSet={currentStructureSet} readOnly />
                </Box>
            )}
        </Box>
    );
}
